import { ClockIcon } from "@/pages/taskpane/components/icons/IconCustom";
import DivShadow from "@/pages/taskpane/components/layout/DivShadow";
import HStack from "@/pages/taskpane/components/layout/HStack";
import VStack from "@/pages/taskpane/components/layout/VStack";
import Button from "@/pages/taskpane/components/ui/Button";
import Status from "@/pages/taskpane/components/ui/Status";
import { useOutlookActions } from "@/pages/taskpane/lib/office/hooks";
import { useFormatDateTime, useScrollShadow } from "@/pages/taskpane/shared/hooks";
import {
  useAppointmentStore,
  useAttendeesStore,
  useOutlookStore,
  useResourceStore,
  useThemeStore,
} from "@/pages/taskpane/shared/stores";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EStatusResource } from "../../resources/services/types/enum";
import {
  convertParsedTimeToString,
  isAllDayMeeting,
  isSameDay,
  ParsedDateTime,
  parseISOString,
} from "../../resources/ulti";
import { useFindAvailableTime } from "../../search/hooks/useFindAvailableTime";
import { GetSuggestTimeInfo } from "../../search/services/entities/suggestTimeEntity";
import { Loader } from "@/pages/taskpane/components/ui/Loader";

type Props = {
  onClose: () => void;
};

type TimeType = {
  start: ParsedDateTime | null;
  end: ParsedDateTime | null;
  startTimeZone: string;
  endTimeZone: string;
  availableCount: number;
  totalCount: number;
  id: string;
  isSelectedSlot: boolean;
};

const ModalSuggetTime = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const { timeZone } = useOutlookStore();
  const { SetAppointmentTime } = useOutlookActions();
  // const showToast = useToastStore((state) => state.showToast);
  const { onGetSuggestTime } = useFindAvailableTime();
  const { requiredAttendees, optionalAttendees, mainAttendee } = useAttendeesStore();
  const { startDateTimeUtc, endDateTimeUtc, startDateTimeZone, endDateTimeZone } = useAppointmentStore();
  const { selectedResource } = useResourceStore();

  const suggestedTimeRef = useRef<HTMLDivElement>(null);
  const [selectedTime, setSelectedTime] = useState<TimeType | null>(null);
  const [suggestedTimes, setSuggestedTimes] = useState<GetSuggestTimeInfo[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isAllDayEvent = useMemo(
    () => isAllDayMeeting(parseISOString(startDateTimeZone), parseISOString(endDateTimeZone)),
    [startDateTimeZone, endDateTimeZone]
  );

  const allAttendees = useMemo(() => {
    return [...(requiredAttendees || []), ...(optionalAttendees || []), ...(mainAttendee ? [mainAttendee] : [])].filter(
      (attendee) => !attendee.isExternal
    );
  }, [requiredAttendees, optionalAttendees, mainAttendee]);

  const handleSelect = (time: TimeType) => {
    setSelectedTime(time);
  };

  const handleChangeTime = () => {
    if (selectedTime) {
      if (selectedTime.startTimeZone && selectedTime.endTimeZone && !selectedTime.isSelectedSlot) {
        SetAppointmentTime(new Date(selectedTime.startTimeZone), new Date(selectedTime.endTimeZone));
      }
      // showToast(t("Time changed successfully"), true);
      onClose();
    }
  };

  const onGetSuggestedTimes = async () => {
    setIsLoading(true);
    try {
      const response: GetSuggestTimeInfo[] = await onGetSuggestTime({
        organizer: mainAttendee?.email || "",
        meetingStartTime: startDateTimeUtc || "",
        meetingEndTime: endDateTimeUtc || "",
        attendees: allAttendees.map((attendee) => attendee.email),
        resourceIds: selectedResource.map((resource) => resource.id),
        timeZoneKeyName: timeZone || "",
        isAllDayMeeting: isAllDayEvent,
      });
      setSuggestedTimes(response);
    } catch (error) {
      console.error("[ModalSuggetTime] Error fetching suggested times:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    onGetSuggestedTimes();
  }, []);

  const listTimes = useMemo(() => {
    const times: TimeType[] = [];

    if (suggestedTimes && suggestedTimes.length > 0) {
      suggestedTimes.forEach((time) => {
        // Validate that startTime and endTime exist and are valid strings
        if (!time.startTime || !time.endTime) {
          console.warn("[ModalSuggetTime] Invalid time data:", time);
          return;
        }

        const parsedStart = parseISOString(time.startTime);
        const parsedEnd = parseISOString(time.endTime);

        // Validate parsed dates (year: 0 means parse failed)
        if (parsedStart.year === 0 || parsedEnd.year === 0) {
          console.warn("[ModalSuggetTime] Failed to parse time:", {
            startTime: time.startTime,
            endTime: time.endTime,
            parsedStart,
            parsedEnd,
          });
          return;
        }
        const timeData = {
          start: parsedStart,
          end: parsedEnd,
          availableCount: time.availableCount,
          totalCount: time.totalCount,
          id: time.id,
          startTimeZone: time.startTime,
          endTimeZone: time.endTime,
          isSelectedSlot: time.isSelectedSlot,
        };

        times.push(timeData);
        if (time.isSelectedSlot) {
          setSelectedTime(timeData);
        }
      });
    }

    times.sort((a, b) => {
      if (!a.start || !b.start) return 0;

      const aTime = new Date(
        a.start.year,
        a.start.month - 1,
        a.start.day,
        a.start.hours,
        a.start.minutes,
        a.start.seconds
      ).getTime();

      const bTime = new Date(
        b.start.year,
        b.start.month - 1,
        b.start.day,
        b.start.hours,
        b.start.minutes,
        b.start.seconds
      ).getTime();

      return aTime - bTime;
    });

    return times;
  }, [suggestedTimes]);

  useScrollShadow(suggestedTimeRef);

  return (
    <VStack justifyContent="between" className="h-full">
      <VStack className="w-full flex-1 overflow-hidden px-5 py-4">
        <div ref={suggestedTimeRef} className="flex-1 overflow-y-auto no-scrollbar w-full">
          <VStack gap={3}>
            {/* suggested times */}
            {isLoading ? (
              <HStack className="w-full justify-center p-10">
                <Loader texts={[t("Loading...")]} />
              </HStack>
            ) : listTimes?.length ? (
              listTimes.map((time) => (
                <TimeItem key={time.id} data={time} selected={selectedTime?.id === time.id} onSelect={handleSelect} />
              ))
            ) : null}
          </VStack>
        </div>
      </VStack>

      <DivShadow className="p-4 bg-veryLightGray dark:bg-charcoalGray flex-shrink-0">
        <Button
          label={t("Change Time")}
          isDefault={true}
          className="text-sm font-semibold py-[6px] w-full"
          onClick={() => handleChangeTime()}
        />
      </DivShadow>
    </VStack>
  );
};

export default ModalSuggetTime;

type TimeItemProps = {
  data: TimeType;
  selected: boolean;
  onSelect: (time: TimeType) => void;
};

const TimeItem = ({ data, selected, onSelect }: TimeItemProps) => {
  const { t } = useTranslation();
  const { isDarkMode } = useThemeStore();
  const { formatDateWithLanguage, handleFormatDateWithSeparator, handleFormatTimeWithLanguage } =
    useFormatDateTime();

  const displayDate = useMemo(() => {
    if (!data.start) return "";

    const suggestedDate = data.start;

    const now = new Date();
    const today: ParsedDateTime = {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow: ParsedDateTime = {
      year: tomorrowDate.getFullYear(),
      month: tomorrowDate.getMonth() + 1,
      day: tomorrowDate.getDate(),
      hours: 0,
      minutes: 0,
      seconds: 0,
    };

    const isToday = isSameDay(suggestedDate, today);
    const isTomorrow = isSameDay(suggestedDate, tomorrow);
    const dateStr = handleFormatDateWithSeparator(convertParsedTimeToString(suggestedDate));
    const dayOfWeek = formatDateWithLanguage(convertParsedTimeToString(suggestedDate), "dddd");
    const displayDate = `${dayOfWeek}, ${dateStr}`;

    if (isToday) {
      return `${t("Today")}, ${displayDate}`;
    }
    if (isTomorrow) {
      return `${t("Tomorrow")}, ${displayDate}`;
    }
    return displayDate;
  }, [data.start, t]);

  const availableText = useMemo(() => {
    let text =
      data.availableCount === data.totalCount
        ? t("All internal attendees are free")
        : t("{{quantity}} internal attendees are free", { quantity: `${data.availableCount}/${data.totalCount}` });
    return text;
  }, [data.availableCount, data.totalCount, t]);

  return (
    <HStack
      className={`w-full p-3 bg-white rounded-[4px] border-[1px] border-neutralGray cursor-pointer dark:border-balancedGray dark:bg-darkBackgroundColor`}
      justifyContent="between"
      onClick={() => onSelect(data)}
    >
      <HStack gap={3}>
        <div className="w-8 h-8 bg-[#ddd] relative rounded-full flex justify-center items-center">
          <ClockIcon isDarkMode={isDarkMode} size={20} />
          <div className="absolute bottom-[-4px] right-[-4px]">
            <Status status={EStatusResource.Available} size={16} />
          </div>
        </div>
        <div>
          {displayDate ? <p className="text-sm font-semibold">{displayDate}</p> : null}
          <p className="text-sm font-semibold">
            {handleFormatTimeWithLanguage(convertParsedTimeToString(data.start))} -{" "}
            {handleFormatTimeWithLanguage(convertParsedTimeToString(data.end))}{" "}
          </p>
          <p className="text-xs text-mediumGray dark:text-midGray">{availableText ? availableText : null}</p>
        </div>
      </HStack>
      <input type="radio" name="meetingTime" checked={selected} readOnly className="w-4 h-4" />
    </HStack>
  );
};
